import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  EVENTS,
  GALLERY_IMAGES,
  FOCUS_AREAS,
  HOME_STATS,
  ORG,
  PROGRAMMES_COPY,
  PROJECT_VIDEOS,
  SERVICES,
  YOUTUBE_CHANNEL,
} from "../data/content";
import { fetchSiteContent } from "../lib/adminApi";
import { normalizeEvent } from "../lib/events";

export const DEFAULT_HERO = {
  body: "Creative Flare Yorkshire is a community-focused organisation led by two qualified teachers with extensive experience in education, training, community engagement, and hospitality. Through a passion for empowering individuals and strengthening communities, Creative Flare Yorkshire has successfully delivered a wide range of programmes designed to tackle social issues, improve health and wellbeing, and create pathways into education and employment.",
};

function persistableImage(src = "") {
  if (!src || String(src).includes("/static/media/")) return "";
  return src;
}

function mergeFocusAreas(remoteList) {
  const fill = (item = {}, local = {}, i) => {
    const remoteBullets = Array.isArray(item.bullets) ? item.bullets.filter(Boolean) : [];
    const bullets = remoteBullets.length ? remoteBullets : local.bullets || [];
    const image = persistableImage(item.image) || local.image || "";
    return {
      id: item.id || local.id || `focus-${i + 1}`,
      title: item.title || local.title || "",
      text: bullets.length && !remoteBullets.length ? local.text || "" : item.text ?? local.text ?? "",
      bullets,
      image,
    };
  };

  if (!Array.isArray(remoteList)) {
    return FOCUS_AREAS.map((item, i) => fill(item, item, i));
  }

  const remote = remoteList.filter(
    (item) => String(item?.title || "").trim().toLowerCase() !== "upcoming workshop"
  );
  const used = new Set();
  return remote
    .map((item, i) => {
      const matchIndex = FOCUS_AREAS.findIndex((local, idx) => {
        if (used.has(idx) || !item?.title || !local.title) return false;
        return String(item.title).trim().toLowerCase() === String(local.title).trim().toLowerCase();
      });
      const local = matchIndex >= 0 ? FOCUS_AREAS[matchIndex] : {};
      if (matchIndex >= 0) used.add(matchIndex);
      return fill(item, local, i);
    })
    .filter((item) => item.title);
}

function mergeRemote(remote = {}) {
  const org = { ...ORG, ...(remote.org || {}) };
  const stats = Array.isArray(remote.home_stats) ? remote.home_stats : HOME_STATS;
  const focusAreas = mergeFocusAreas(remote.focus_areas);
  const events = mergeEvents(remote.events);
  const gallery = mergeGallery(remote.gallery);
  const services = mergeServices(SERVICES, remote.services);
  const programmes = { ...PROGRAMMES_COPY, ...(remote.programmes || {}) };
  const hero = { ...DEFAULT_HERO, ...(remote.hero || {}) };
  const videos = mergeVideos(remote.videos);
  return { org, stats, focusAreas, events, gallery, services, programmes, hero, videos, raw: remote };
}

function mergeEvents(remote) {
  const list = Array.isArray(remote) ? remote : Array.isArray(remote?.items) ? remote.items : null;
  const source = list && list.length ? list : EVENTS;
  return source.map((item, i) => normalizeEvent(item, i)).filter((item) => item.title && item.date);
}

function mergeGallery(remote) {
  const list = Array.isArray(remote) ? remote : Array.isArray(remote?.items) ? remote.items : null;
  const localById = Object.fromEntries(GALLERY_IMAGES.map((item) => [item.id, item]));

  if (list == null) {
    return GALLERY_IMAGES.map((item) => ({ ...item }));
  }

  return list
    .map((item, i) => {
      const local = localById[item?.id] || {};
      return {
        id: item.id || local.id || `gal-${i + 1}`,
        src: persistableImage(item?.src) || local.src || "",
        caption: item?.caption ?? local.caption ?? "",
      };
    })
    .filter((item) => item.src);
}

function defaultVideoItems() {
  return PROJECT_VIDEOS.map((video, i) => ({
    id: video.id,
    title: video.title,
    caption: video.caption || "",
    src: video.src,
    showOnHome: video.showOnHome ?? i === 0,
    showOnServices: video.showOnServices !== false,
  }));
}

function mergeVideos(remote) {
  const localItems = defaultVideoItems();
  const youtubeChannel = remote?.youtubeChannel || YOUTUBE_CHANNEL;
  const byId = Object.fromEntries(localItems.map((item) => [item.id, item]));

  const fill = (item = {}, i, fallback) => ({
    id: item.id || fallback?.id || `video-${i + 1}`,
    title: item.title || fallback?.title || `Video ${i + 1}`,
    caption: item.caption ?? fallback?.caption ?? "",
    src: item.src || fallback?.src || "",
    showOnHome: Boolean(item.showOnHome),
    showOnServices: item.showOnServices !== false,
  });

  let homeVideo;
  let serviceVideos;

  if (remote?.homeVideo || Array.isArray(remote?.serviceVideos)) {
    homeVideo = fill(
      { ...remote.homeVideo, showOnHome: true, showOnServices: remote.homeVideo?.showOnServices },
      0,
      byId[remote.homeVideo?.id] || localItems[0]
    );
    serviceVideos = (remote.serviceVideos || []).map((item, i) =>
      fill({ ...item, showOnHome: false, showOnServices: true }, i + 1, byId[item.id])
    );
  } else if (Array.isArray(remote?.items) && remote.items.length > 0) {
    const items = remote.items.map((item, i) => fill(item, i, byId[item.id] || localItems[i]));
    homeVideo = fill(
      { ...(items.find((item) => item.showOnHome) || items[0]), showOnHome: true },
      0,
      localItems[0]
    );
    serviceVideos = items
      .filter((item) => item.id !== homeVideo.id && item.showOnServices !== false)
      .map((item, i) => fill({ ...item, showOnHome: false, showOnServices: true }, i + 1, byId[item.id]));
  } else {
    homeVideo = fill({ ...localItems[0], showOnHome: true, showOnServices: true }, 0, localItems[0]);
    serviceVideos = localItems.slice(1).map((item, i) =>
      fill({ ...item, showOnHome: false, showOnServices: true }, i + 1, item)
    );
  }

  const items = [
    { ...homeVideo, showOnHome: true },
    ...serviceVideos.map((item) => ({ ...item, showOnHome: false, showOnServices: true })),
  ];

  return { youtubeChannel, homeVideo, serviceVideos, items };
}

function mergeServices(defaults, remoteList) {
  const remote = Array.isArray(remoteList) ? remoteList : [];

  if (remote.length >= defaults.length) {
    return remote.map((item, i) => {
      const local = defaults[i] || {};
      return {
        ...local,
        ...item,
        image: local.image || item.image,
        images: local.images?.length ? local.images : item.images || [local.image || item.image].filter(Boolean),
        accent: item.accent || local.accent || (i % 2 ? "black" : "primary"),
        bullets: Array.isArray(item.bullets) ? item.bullets.filter(Boolean) : local.bullets,
      };
    });
  }

  const used = new Set();
  return defaults.map((local) => {
    const matchIndex = remote.findIndex((item, i) => {
      if (used.has(i) || !item?.title || !local.title) return false;
      return String(item.title).trim().toLowerCase() === String(local.title).trim().toLowerCase();
    });
    const item = matchIndex >= 0 ? remote[matchIndex] : {};
    if (matchIndex >= 0) used.add(matchIndex);

    const remoteBullets = Array.isArray(item.bullets) ? item.bullets.filter(Boolean) : [];
    const useRemoteCopy = matchIndex >= 0 && (remoteBullets.length > 0 || !local.bullets?.length);

    return {
      ...local,
      ...item,
      title: local.title,
      image: local.image || item.image,
      images: local.images?.length ? local.images : item.images || [local.image || item.image].filter(Boolean),
      accent: item.accent || local.accent,
      text: useRemoteCopy ? (item.text || local.text) : local.text,
      bullets: remoteBullets.length ? remoteBullets : local.bullets,
    };
  });
}

const SiteContentContext = createContext(null);

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(() => mergeRemote());
  const [loaded, setLoaded] = useState(false);

  const refresh = async () => {
    try {
      const remote = await fetchSiteContent();
      setContent(mergeRemote(remote));
    } catch (err) {
      console.warn("Could not load live site content:", err.message);
      setContent(mergeRemote());
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const value = useMemo(() => ({ ...content, loaded, refresh }), [content, loaded]);

  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
}

export function useSiteContent() {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error("useSiteContent must be used within SiteContentProvider");
  return ctx;
}
